import React from 'react';
import _get from 'lodash/get';
import _size from 'lodash/size';
import _isArray from 'lodash/isArray';
import _toLower from 'lodash/toLower';
import { ReactComponent as DownSmall } from 'assets/img/icons/down-small.svg';

import { formatPrice } from 'utils/formatters';
import { TableActions, TableHighlight } from 'components/Tables';
import createPriceRange from './createPriceRange';
import { CONFIGURED_SKUS_TABLE_HEADERS as HEADERS } from '../stubs';
import { PRODUCT_SKU_CONFIG_KEYS as DATA_KEYS } from 'views/Configure/ReturnManagement/ReturnWindow/stubs';

export default function createConfiguredSkusTableRowsData(
  products = [],
  searchQuery = '',
  onFormDataChange = () => {}
) {
  if (!_isArray(products)) return [];

  const onRemoveProduct = productId => {
    if (!productId) return;
    const newProductsList = products.filter(product => {
      return productId !== _get(product, 'ObjectId');
    });
    onFormDataChange(DATA_KEYS.PRODUCTS, newProductsList);
  };

  const onRemoveProductVariant = (productId, productVariantId) => {
    if (!productId || !productVariantId) return;

    const newProductsList = products.map(product => {
      if (productId !== _get(product, 'ObjectId')) return product;

      const productVariants = _get(product, 'productVariants', []);
      const newProductVariants = productVariants.filter(
        productVariant => productVariantId !== _get(productVariant, 'objectID')
      );
      return {
        ...product,
        productVariants: newProductVariants
      };
    });

    onFormDataChange(DATA_KEYS.PRODUCTS, newProductsList);
  };

  return products.reduce((acc, product) => {
    const id = _get(product, 'ObjectId', '');
    const productName = _toLower(_get(product, 'title', ''));
    const currency = _get(product, 'currency', '');
    const variants = _get(product, 'productVariants', []);
    const isVisible = productName.includes(_toLower(searchQuery));

    if (isVisible) {
      const data = {
        rowData: createProductRowData(product, searchQuery, onRemoveProduct),
        collapsedRowsData: createProductVariantRowData(
          variants,
          currency,
          productVariantId => onRemoveProductVariant(id, productVariantId)
        )
      };
      acc.push(data);
    }

    return acc;
  }, []);
}

function createProductRowData(
  product = {},
  searchQuery = '',
  onRemoveProduct = () => {}
) {
  const id = _get(product, 'ObjectId');

  const imageUrl = _get(product, 'image_url', '');
  const priceRange = _get(product, 'price_range', []);
  const currency = _get(product, 'currency', '');
  const productVariants = _get(product, 'productVariants', []);
  const formattedPriceRange = createPriceRange(priceRange, currency);

  return {
    id,
    values: [
      {
        lg: HEADERS[0].lg,
        CustomComponent: (
          <div className="product-title-cell">
            <div style={{ backgroundImage: `url(${imageUrl})` }} />
            <TableHighlight
              text={searchQuery}
              data={product}
              attribute="title"
              className="truncate"
            />
            <span>{_size(productVariants)} variants</span>
            <DownSmall />
          </div>
        )
      },
      {
        lg: HEADERS[1].lg,
        title: ''
      },
      {
        lg: HEADERS[2].lg,
        title: formattedPriceRange,
        customClass: 'light-text'
      },
      {
        lg: HEADERS[3].lg,
        customClass: 'text-right',
        CustomComponent: (
          <TableActions
            id={id}
            permissions={{ delete: true }}
            remove={onRemoveProduct}
          />
        )
      }
    ]
  };
}

function createProductVariantRowData(
  productVariants = [],
  currency = '',
  onRemoveProductVariant = () => {}
) {
  return productVariants.map(productVariant => {
    const id = _get(productVariant, 'objectID');
    const productVariantName = _get(productVariant, 'title', '');
    const imageUrl = _get(productVariant, 'image_url', '');
    const price = _get(productVariant, 'price', '');
    const sku = _get(productVariant, 'sku', '');

    return {
      id,
      values: [
        {
          lg: HEADERS[0].lg,
          CustomComponent: (
            <div className="product-variant-title-cell">
              <div style={{ backgroundImage: `url(${imageUrl})` }} />
              <p>{productVariantName}</p>
            </div>
          )
        },
        {
          lg: HEADERS[1].lg,
          title: sku,
          customClass: 'light-text'
        },
        {
          lg: HEADERS[2].lg,
          title: formatPrice(price, currency),
          customClass: 'light-text'
        },
        {
          lg: HEADERS[3].lg,
          customClass: 'text-right',
          CustomComponent: (
            <TableActions
              id={id}
              permissions={{ delete: true }}
              remove={onRemoveProductVariant}
            />
          )
        }
      ]
    };
  });
}
